import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Church, Loader2, CheckCircle, ArrowRight, User, Lock, Mail, Phone, MapPin, Building2 } from 'lucide-react';

// Backend URL - Use environment variable in production
const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : (window.location.origin.includes('localhost')
    ? 'http://localhost:8001/api'
    : `${window.location.origin}/api`);

const ChurchRegistration = ({ onBack, onSuccess }) => {
  const [step, setStep] = useState(1); // 1: Church Info, 2: Admin Info, 3: Success
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Church Info
  const [churchName, setChurchName] = useState('');
  const [churchNameShort, setChurchNameShort] = useState('');
  const [churchLocation, setChurchLocation] = useState('');
  const [diocese, setDiocese] = useState('');
  
  // Admin Info
  const [adminUsername, setAdminUsername] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminPasswordConfirm, setAdminPasswordConfirm] = useState('');
  const [adminFullName, setAdminFullName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPhone, setAdminPhone] = useState('');
  
  // Success data
  const [registrationResult, setRegistrationResult] = useState(null);

  const validateStep1 = () => {
    if (!churchName.trim()) {
      setError('يرجى إدخال اسم الكنيسة');
      return false;
    }
    setError('');
    return true;
  };

  const validateStep2 = () => {
    if (!adminUsername.trim()) {
      setError('يرجى إدخال اسم المستخدم للمدير');
      return false;
    }
    if (adminUsername.length < 3) {
      setError('اسم المستخدم يجب أن يكون 3 أحرف على الأقل');
      return false;
    }
    if (!adminPassword) {
      setError('يرجى إدخال كلمة المرور');
      return false;
    }
    if (adminPassword.length < 6) {
      setError('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      return false;
    }
    if (adminPassword !== adminPasswordConfirm) {
      setError('كلمات المرور غير متطابقة');
      return false;
    }
    setError('');
    return true;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep2()) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/churches/register`, {
        name: churchName,
        name_short: churchNameShort || null,
        location: churchLocation || null,
        diocese: diocese || null,
        admin_username: adminUsername,
        admin_password: adminPassword,
        admin_email: adminEmail || null,
        admin_full_name: adminFullName || null,
        admin_phone: adminPhone || null
      });
      
      setRegistrationResult(response.data);
      setStep(3);
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.response?.data?.detail || 'حدث خطأ أثناء التسجيل. يرجى المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 p-4" dir="rtl">
      <Card className="w-full max-w-2xl shadow-2xl border-amber-200">
        <CardHeader className="bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-t-lg">
          <CardTitle className="text-center text-2xl font-bold flex items-center justify-center gap-3">
            <Church className="w-8 h-8" />
            تسجيل كنيسة جديدة
          </CardTitle>
          {step < 3 && (
            <div className="flex justify-center mt-4">
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  step >= 1 ? 'bg-white text-amber-600' : 'bg-amber-400 text-white'
                }`}>1</div>
                <div className={`w-16 h-1 ${step >= 2 ? 'bg-white' : 'bg-amber-400'}`}></div>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  step >= 2 ? 'bg-white text-amber-600' : 'bg-amber-400 text-white'
                }`}>2</div>
              </div>
            </div>
          )}
        </CardHeader>
        
        <CardContent className="p-8">
          {/* Step 1: Church Information */}
          {step === 1 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">معلومات الكنيسة</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Building2 className="w-4 h-4 inline ml-1" />
                  اسم الكنيسة <span className="text-red-500">*</span>
                </label>
                <Input
                  value={churchName}
                  onChange={(e) => setChurchName(e.target.value)}
                  placeholder="مثال: كنيسة السيدة العذراء مريم"
                  className="text-right"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  اسم مختصر (اختياري)
                </label>
                <Input
                  value={churchNameShort}
                  onChange={(e) => setChurchNameShort(e.target.value)}
                  placeholder="مثال: كنيسة العذراء"
                  className="text-right"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="w-4 h-4 inline ml-1" />
                  الموقع / المدينة (اختياري)
                </label>
                <Input
                  value={churchLocation}
                  onChange={(e) => setChurchLocation(e.target.value)}
                  placeholder="مثال: القاهرة - مصر الجديدة"
                  className="text-right"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  الإيبارشية (اختياري)
                </label>
                <Input
                  value={diocese}
                  onChange={(e) => setDiocese(e.target.value)}
                  placeholder="مثال: إيبارشية القاهرة"
                  className="text-right"
                />
              </div>
              
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}
              
              <div className="flex gap-4 pt-4">
                <Button
                  onClick={onBack}
                  variant="outline"
                  className="flex-1"
                >
                  <ArrowRight className="w-4 h-4 ml-2" />
                  العودة
                </Button>
                <Button
                  onClick={handleNext}
                  className="flex-1 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700"
                >
                  التالي
                </Button>
              </div>
            </div>
          )}
          
          {/* Step 2: Admin Information */}
          {step === 2 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">معلومات المدير</h3>
              <p className="text-sm text-gray-500 mb-4">
                هذا الحساب سيكون مدير الكنيسة ويمكنه إدارة جميع البيانات
              </p>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <User className="w-4 h-4 inline ml-1" />
                  اسم المستخدم <span className="text-red-500">*</span>
                </label>
                <Input
                  value={adminUsername}
                  onChange={(e) => setAdminUsername(e.target.value)}
                  placeholder="اسم المستخدم لتسجيل الدخول"
                  className="text-right"
                  dir="ltr"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  الاسم الكامل (اختياري)
                </label>
                <Input
                  value={adminFullName}
                  onChange={(e) => setAdminFullName(e.target.value)}
                  placeholder="مثال: الأب يوحنا"
                  className="text-right"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Lock className="w-4 h-4 inline ml-1" />
                    كلمة المرور <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="password"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    placeholder="كلمة المرور"
                    className="text-right"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    تأكيد كلمة المرور <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="password"
                    value={adminPasswordConfirm}
                    onChange={(e) => setAdminPasswordConfirm(e.target.value)}
                    placeholder="أعد إدخال كلمة المرور"
                    className="text-right"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Mail className="w-4 h-4 inline ml-1" />
                  البريد الإلكتروني (اختياري)
                </label>
                <Input
                  type="email"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  placeholder="email@example.com"
                  className="text-right"
                  dir="ltr"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Phone className="w-4 h-4 inline ml-1" />
                  رقم الهاتف (اختياري)
                </label>
                <Input
                  value={adminPhone}
                  onChange={(e) => setAdminPhone(e.target.value)}
                  placeholder="+20 123 456 7890"
                  className="text-right"
                  dir="ltr"
                />
              </div>
              
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}
              
              <div className="flex gap-4 pt-4">
                <Button
                  onClick={() => setStep(1)}
                  variant="outline"
                  className="flex-1"
                  disabled={loading}
                >
                  السابق
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 ml-2 animate-spin" />
                      جاري التسجيل...
                    </>
                  ) : (
                    <>
                      <Church className="w-4 h-4 ml-2" />
                      تسجيل الكنيسة
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
          
          {/* Step 3: Success */}
          {step === 3 && registrationResult && (
            <div className="text-center space-y-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-12 h-12 text-green-500" />
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-gray-800">تم التسجيل بنجاح!</h3>
                <p className="text-gray-600 mt-2">تم تسجيل الكنيسة وإنشاء حساب المدير</p>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-right">
                <h4 className="font-semibold text-amber-800 mb-3">بيانات الدخول:</h4>
                <div className="space-y-2 text-sm">
                  <p><span className="text-gray-600">اسم الكنيسة:</span> <strong>{registrationResult.church_name}</strong></p>
                  <p><span className="text-gray-600">رقم الكنيسة:</span> <strong dir="ltr">{registrationResult.church_id}</strong></p>
                  <p><span className="text-gray-600">اسم المستخدم:</span> <strong dir="ltr">{registrationResult.admin_username}</strong></p>
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-700">
                <strong>ملاحظة:</strong> يرجى الاحتفاظ ببيانات الدخول في مكان آمن
              </div>
              
              <Button
                onClick={onSuccess}
                className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700"
              >
                الذهاب لتسجيل الدخول
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ChurchRegistration;
